import React from 'react';

import { formatDecimal } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import { Currency } from '@/types/currency';
import { Store } from '@/types/store';
import { useAtomValue } from 'jotai';

import { TableBasicInput } from './table-basic-input';

export interface TablePriceInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  hasError?: boolean;
}

/**
 * Specialized price input for table inline editing
 * Handles currency formatting, validation, and comma-to-dot conversion
 */
export const TablePriceInput: React.FC<TablePriceInputProps> = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  disabled,
  autoFocus,
  className,
  hasError = false,
}) => {
  const store = useAtomValue(storeAtom);
  const currency = store === Store.FR ? Currency.EUR : Currency.USD;

  // Format the value for display: convert number to string with comma as decimal separator
  const displayValue = formatDecimal(value);

  return (
    <div className="relative flex h-full w-full items-center">
      <TableBasicInput
        value={displayValue}
        onChange={(strValue) => {
          if (strValue === null) {
            onChange(null);
          } else {
            const val = strValue.replace(',', '.');
            const parsed = isNaN(parseFloat(val)) ? null : parseFloat(val);
            if (parsed !== value) {
              onChange(parsed);
            }
          }
        }}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
        className={className}
        hasError={hasError}
        align="right"
        onInputChange={(e) => {
          // Allow only numbers, comma, and dot
          if (
            /[^0-9.,]/.test(e.target.value) &&
            e.target.value !== ',' &&
            e.target.value !== '.'
          ) {
            e.target.value = e.target.value.replace(/[^0-9.,]/, '');
          }
        }}
      />
      <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 select-none text-base text-gray-500">
        {currency}
      </span>
    </div>
  );
};
